import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faX } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { sendTemporaryCredentialsToWorker, updateProfilePhoto, updateUserNIN } from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { format } from "date-fns";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import Competencies from "./Competencies/Competencies";
import Experiences from "./Experiences/Experiences";
import Ticket from "./Tickets/Tickets";
import Maps from "./Map/Map";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import { getTierStatus } from "../../../utils/utils";
import LatestContactHistory from "./Contact History/LatestContactHistory";
import WorkRole from "./WorkRole/WorkRole";
import WorkerIdenticationsType from "./WorkerIdentificationsType/WorkerIdenticationsType";
import AddIcon from "@mui/icons-material/Add";
import OnboardingForms from "./OnboardingForms/OnboardingForms";
import EmailIcon from "@mui/icons-material/Email";
import {Modal} from 'antd';

const Profile = ({ profileInfo, setProfileInfo, getWorkerInfo }: any) => {
    const { state } = useAuth();
    const navigate = useNavigate();
    const [image, setImage] = useState<any>(null);
    const [open, setOpen] = useState(false);
    const [ninEditOpen, setNinEditOpen] = useState(false);
    const [newNin, setNewNin] = useState("");
    const [updatingNin, setUpdatingNin] = useState(false);
    const [ninError, setNinError] = useState(false);
    const [ninHelperText, setNinHelperText] = useState("");

    const ninRegex = /^(?!BG)(?!GB)(?!NK)(?!KN)(?!TN)(?!NT)(?!ZZ)(?:[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z])(?:\s*\d\s*){6}([A-D]|\s)$/i;

    const handleImageUpload = async (event: any) => {
        const file = event.target.files[0];
        if (file) {
            const response = await updateProfilePhoto(state.user?.token, profileInfo.id, file);
            if(response) {
                toast.success("Profile updated successfully");
            }
            setImage(file);
        }
    };

    const handleEdit = async () => {
        navigate("/pace-team/resourcing/edit-worker", {
            state: { workerId: profileInfo?.id },
        });
    };

    const handleNinEditClick = () => {
        setNewNin(profileInfo?.nationalInsuranceId || "");
        setNinEditOpen(true);
    };

    const handleNinUpdate = async () => {
        if (!newNin.trim()) {
            toast.error("NIN cannot be empty");
            return;
        }

        setUpdatingNin(true);
        try {
            const response = await updateUserNIN(state.user?.token, profileInfo.id, newNin);
            if (!response) throw new Error("Failed to update NIN");
            toast.success("NIN updated successfully");
            setNinEditOpen(false);
            await getWorkerInfo();
        } catch (error) {
            console.error(error);
            toast.error("Error updating NIN");
        } finally {
            setUpdatingNin(false);
        }
    };

    const handleSendCredentials = async () => {
        try {
            const response = await sendTemporaryCredentialsToWorker(state.user?.token, profileInfo.id);
            if (response.success) {
                toast.success("Temporary credentials sent successfully");
            }
        } catch (error) {
            toast.error("Error sending temporary credentials");
        }
    };

    return (
        <>
            <Box
                mt={7}
                gap={2}
                display="grid"
                gridTemplateColumns={{xs: "1fr", md: "1fr 1fr 1fr 1fr 1.25fr"}}
                sx={{
                    backgroundColor: "#fff",
                    padding: "50px",
                    margin: "auto",
                    background: "#fff",
                    marginTop: 5,
                    border: "1px solid rgb(236, 236, 236)",
                }}
            >
                <Grid item>
                    <Typography variant="h6" fontWeight="bold" mb={4}>
                        Worker's Details
                    </Typography>
                    <Box position="relative" display="inline-block">
                        <Avatar
                            sx={{ width: 160, height: 160 }}
                            src={image ? URL.createObjectURL(image) : "data:image/png;base64," + profileInfo?.picture}
                        />
                        <IconButton
                            component="label"
                            sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                bgcolor: "background.paper",
                                borderRadius: "50%",
                                padding: "5px",
                            }}
                        >
                            <EditIcon />
                            <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                        </IconButton>
                    </Box>
                    <Box mt={4}>
                        <Typography variant="body1">
                            <strong>NIN: </strong> {profileInfo?.nationalInsuranceId || "—"}
                            <IconButton
                                sx={{ m: -0.5, ml: 0.5 }}
                                size="small"
                                onClick={handleNinEditClick}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Typography>
                        <Typography variant="body1" mt={1}>
                            <strong>Username:</strong> {profileInfo?.username}{" "}
                            {profileInfo?.verified ? (
                                <GppGoodOutlinedIcon sx={{ color: "green", m: -0.5, ml: 0 }} fontSize="small" />
                            ) : (
                                " (Not Verified)"
                            )}
                        </Typography>
                        <Typography variant="body1" mt={1}>
                            <strong>Date of Birth: </strong>
                            {format(new Date(profileInfo?.dateOfBirth || Date.now()), "dd/MM/yyyy")}
                        </Typography>
                        <Typography variant="body1" mt={1}>
                            <strong>Created Date: </strong>{" "}
                            {format(new Date(profileInfo?.createdDate || Date.now()), "dd/MM/yyyy")}
                        </Typography>
                        <Typography variant="body1" mt={1}>
                            <strong>Created by: </strong> {profileInfo.createdByFullName}
                        </Typography>
                        <Button
                            variant="contained"
                            sx={{ boxShadow: "none", width: "100px", marginTop: 4 }}
                            onClick={handleEdit}
                        >
                            Edit
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs>
                    <Typography variant="body1" mb={2} mt={4}>
                        <strong>Name</strong>
                        <Tooltip title="Send mobile login credentials via Email" arrow>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    Modal.confirm({
                                        title: 'Send Temporary Credentials?',
                                        content: (
                                            <>
                                                This action will reset the worker's current password and generate a temporary password.
                                                <br />
                                                <br />
                                                The new login credentials will be sent to the worker's registered email address. The worker will need to use these credentials to sign in and may be required to change their password afterwards.
                                            </>
                                        ),
                                        onOk: () => handleSendCredentials(),
                                        okButtonProps: { danger: true },
                                        okText: 'SEND',
                                        cancelText: 'CANCEL',
                                        centered: true,
                                    });
                                }}
                            >
                                <EmailIcon />
                            </IconButton>
                        </Tooltip>
                        <Typography>
                            {profileInfo?.name} {profileInfo?.surname}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Driver:{" "}
                            {profileInfo?.driver ? (
                                <FontAwesomeIcon icon={faCheck} color="green" size="sm" />
                            ) : (
                                <FontAwesomeIcon icon={faX} color="red" size="sm" />
                            )}
                        </Typography>
                    </Typography>

                    <Typography variant="body1" mb={4}>
                        <strong>Number</strong>
                        <IconButton onClick={() => setOpen(true)} size="small">
                            <AddIcon />
                        </IconButton>
                    <Typography>
                        PTS:
                        <Typography component="span" color="orangered" fontWeight="bold">
                            {" "}
                            {profileInfo?.pts}
                        </Typography>
                        </Typography>
                        {profileInfo?.identifications?.map((item: any) => (
                            <Typography key={item.id}>
                                {item?.identificationType?.name}:{" "}
                                <Typography component="span" color="orangered" fontWeight="bold">
                                    {item.number}
                                </Typography>
                            </Typography>
                        ))}
                    </Typography>
                    <Typography variant="body1" mb={4}>
                        <strong>Phone Number</strong>
                        <Typography>{profileInfo?.phoneNumber}</Typography>
                    </Typography>
                    <Typography variant="body1">
                        <strong>Gender</strong>
                        <Typography>{profileInfo?.gender || "—"}</Typography>
                    </Typography>
                </Grid>
                <Grid item xs>
                    <Typography variant="body1" mb={4} mt={4}>
                        <strong>Email</strong>
                        <Typography>{profileInfo?.email}</Typography>
                    </Typography>
                    <Typography variant="body1" mb={4}>
                        <strong>Post Code</strong>
                        <Typography>{profileInfo?.postCode}</Typography>
                    </Typography>
                    <Typography variant="body1" mb={4}>
                        <strong>Location</strong>
                        <Typography>{profileInfo?.location}</Typography>
                    </Typography>
                    <Typography variant="body1" mb={4}>
                        <strong>Nationality</strong>
                        <Typography>{profileInfo?.nationality || "—"}</Typography>
                    </Typography>
                </Grid>
                <Grid item xs>
                    <Typography variant="body1" mb={4} mt={4}>
                        <strong>Tier</strong>
                        <Typography>{getTierStatus(profileInfo?.tier)}</Typography>
                    </Typography>
                    <Typography variant="body1" mb={4}>
                        <strong>Notes</strong>
                        <Typography>{profileInfo?.notes || "—"}</Typography>
                    </Typography>
                    <Typography variant="body1" mb={4}>
                        <strong>Has Disability</strong>
                        <Typography>{profileInfo?.hasDisability ? "Yes" : "No"}</Typography>
                        {profileInfo?.hasDisability && profileInfo?.disabilityDetails && (
                            <Typography variant="body2" color="textSecondary">
                                Details: {profileInfo.disabilityDetails}
                            </Typography>
                        )}
                    </Typography>
                    <Typography variant="body1" mb={4}>
                        <strong>Has Criminal Record</strong>
                        <Typography>{profileInfo?.hasCriminalRecord ? "Yes" : "No"}</Typography>
                        {profileInfo?.hasCriminalRecord && profileInfo?.criminalRecordSpent !== undefined && (
                            <Typography variant="body2" color="textSecondary">
                                Spent: {profileInfo.criminalRecordSpent ? "Yes" : "No"}
                            </Typography>
                        )}
                    </Typography>
                </Grid>
                <Grid item xs>
                    <LatestContactHistory
                        items={profileInfo?.callHistories}
                        profileInfo={profileInfo}
                        getWorkerInfo={getWorkerInfo}
                    />
                </Grid>
            </Box>
            <Box
                sx={{
                    margin: "auto",
                    padding: 4,
                    background: "#fff",
                    marginTop: 5,
                    border: "1px solid rgb(236, 236, 236)",
                }}>
                <WorkRole profileInfo={profileInfo} getWorkerInfo={getWorkerInfo} />
            </Box>
            <Box
                sx={{
                    margin: "auto",
                    padding: 4,
                    background: "#fff",
                    marginTop: 5,
                    border: "1px solid rgb(236, 236, 236)",
                }}>
                <Competencies profileInfo={profileInfo} getWorkerInfo={getWorkerInfo} />
            </Box>
            <Box
                sx={{
                    margin: "auto",
                    padding: 4,
                    background: "#fff",
                    marginTop: 5,
                    border: "1px solid rgb(236, 236, 236)",
                }}
            >
                <Ticket profileInfo={profileInfo} getWorkerInfo={getWorkerInfo} />
            </Box>
            <Box
                sx={{
                    margin: "auto",
                    padding: 4,
                    background: "#fff",
                    marginTop: 5,
                    border: "1px solid rgb(236, 236, 236)",
                }}
            >
                <OnboardingForms profileInfo={profileInfo} getWorkerInfo={getWorkerInfo} />
            </Box>
            <Box
                sx={{
                    margin: "auto",
                    padding: 4,
                    background: "#fff",
                    marginTop: 5,
                    border: "1px solid rgb(236, 236, 236)",
                }}
            >
                <Experiences profileInfo={profileInfo} getWorkerInfo={getWorkerInfo} />
            </Box>
            <Box
                sx={{
                    margin: "auto",
                    padding: 4,
                    background: "#fff",
                    marginTop: 5,
                    border: "1px solid rgb(236, 236, 236)",
                }}
            >
                <Box>
                    <h2>Map Location</h2>
                </Box>
                <Maps profileInfo={profileInfo} />
            </Box>

            <WorkerIdenticationsType open={open} onClose={() => setOpen(false)} workerId={profileInfo?.id} />

           <Dialog open={ninEditOpen} onClose={() => setNinEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit National Insurance Number (NIN)</DialogTitle>
                    <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
                        <strong>Sensitive information:</strong> The NIN is a sensitive personal identifier.
                        Please double‑check the number before saving.
                    </Alert>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="NIN"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newNin}
                        onChange={(e) => {
                            const value = e.target.value;
                            setNewNin(value);
                            if (!value) {
                                setNinError(false);
                                setNinHelperText("");
                            } else if (!ninRegex.test(value)) {
                                setNinError(true);
                                setNinHelperText("Invalid NIN format. Please enter a valid NIN (e.g., AB123456C).");
                            } else {
                                setNinError(false);
                                setNinHelperText("");
                            }
                        }}
                        placeholder="e.g., AB123456C"
                        error={ninError}
                        helperText={ninHelperText}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNinEditOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleNinUpdate} 
                        variant="contained" 
                        disabled={updatingNin || ninError} 
                        style={{ boxShadow: "none" }}
                    >
                        {updatingNin ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default Profile;
